import React, { useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Plan2PlateIcon } from "./Plan2PlateIcon";
import { plan2plateIcons } from "../utils/iconUtils";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { DrawerHistoryContextType } from "../contexts";
import { LoadingSpinner } from "./LoadingSpinner";
import { QueryBoundary } from "./ErrorBoundary";
import { cn } from "../lib/utils";
import { Glyph } from "./SmallIconButton";

export const usePlan2PlateDisclosure = (
  context: Pick<
    DrawerHistoryContextType,
    | "pushHistory"
    | "popHistory"
    | "hasHistory"
    | "history"
    | "closeDrawer"
    | "setWindowHistoryStackSize"
  >,
  name?: string,
) => {
  const [identifier, setIdentifier] = useState(uuidv4());
  const navigate = useNavigate();

  const onOpen = () => {
    const uuid = uuidv4();
    setIdentifier(uuid);
    const state = { id: uuid, name };
    navigate("", { state: state });
    context.setWindowHistoryStackSize(window.history.length + 1);
    context.pushHistory(state);
  };

  const onClose = () => {
    if (!!context.history.length) {
      context.closeDrawer();
    }
  };

  const isOpen = context.hasHistory(identifier);
  return { isOpen: isOpen, onOpen, onClose };
};

export const Plan2PlateDrawer = React.forwardRef<
  HTMLDivElement,
  {
    onClose: () => void;
    open: boolean;
    header?: React.ReactElement;
    body?: React.ReactElement | null;
    footer?: React.ReactElement;
    noScroll?: boolean;
  }
>((props, ref) => {
  return (
    <DialogPrimitive.Root
      open={props.open}
      onOpenChange={(v) => !v && props.onClose()}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-overlay data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out" />
        <DialogPrimitive.Content
          onInteractOutside={(e) => e.preventDefault()}
          aria-describedby={undefined}
          className="fixed inset-0 z-50 mx-auto flex h-[100dvh] w-full flex-col bg-surface-card outline-none data-[state=open]:animate-drawer-open data-[state=closed]:animate-drawer-close"
        >
          <div className="flex items-center gap-2 border-b border-line px-4 py-3">
            <DialogPrimitive.Close
              aria-label="Schließen"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-ink-muted hover:bg-surface"
            >
              ✕
            </DialogPrimitive.Close>
            <DialogPrimitive.Title asChild>
              <div className="flex min-w-0 flex-1 items-center">
                <QueryBoundary fallback={null}>{props.header}</QueryBoundary>
              </div>
            </DialogPrimitive.Title>
            <LoadingSpinner />
          </div>

          <div ref={ref} className="flex-1 min-h-0 overflow-y-auto py-4">
            <div
              className={cn(
                "flex w-full flex-col gap-4 px-4",
                props.noScroll ? "h-full" : "",
              )}
            >
              <QueryBoundary>{props.body}</QueryBoundary>
            </div>
          </div>

          {props.footer && (
            <div className="border-t border-line px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              <QueryBoundary fallback={null}>{props.footer}</QueryBoundary>
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
});
Plan2PlateDrawer.displayName = "Plan2PlateDrawer";

export function Plan2PlateDrawerHeader(props: {
  heading: string;
  dataCy?: string;
}) {
  return (
    <div
      data-cy={props.dataCy}
      className="text-base font-bold"
      style={{ hyphens: "manual" }}
    >
      {props.heading}
    </div>
  );
}

export function DrawerSelection(props: {
  onClick: () => void;
  icon: keyof typeof plan2plateIcons;
  heading: string;
  description: string;
  dataCy?: string;
}) {
  return (
    <div
      className="w-full cursor-pointer rounded-card border border-line bg-surface-card p-4 shadow-card"
      data-cy={props.dataCy}
      onClick={props.onClick}
    >
      <div className="flex items-start gap-3">
        <Glyph size={38} icon={props.icon} />
        <div className="flex flex-col gap-0">
          <div className="text-ink">{props.heading}</div>
          <div className="text-sm text-ink-muted">{props.description}</div>
        </div>
      </div>
    </div>
  );
}
